import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';

import { auth, db } from '../../firebase/firebase';

import {
  UserArenaStats,
  UserData,
  UserSeasonStats,
  UseUserDataReturn,
} from './useUserData.props';

type WalletDoc = {
  leagueId: string;
  seasonId: number;
  type: 'main' | 'arena';
  available: number;
  purchased: number;
  gifted: number;
  earned: number;
  claimed: number[];
};

export const useUserData = (): UseUserDataReturn => {
  const [baseUserData, setBaseUserData] = useState<UserData | null>(null);
  const [wallets, setWallets] = useState<WalletDoc[]>([]);
  const [loading, setLoading] = useState(true);

  const user = auth.currentUser;

  // ---------------------------
  // USER DOC
  // ---------------------------
  useEffect(() => {
    if (!user) {
      setBaseUserData(null);
      setWallets([]);
      setLoading(false);
      return;
    }

    const refUser = doc(db, 'users', user.uid);

    const unsubscribe = onSnapshot(refUser, (snapshot) => {
      if (snapshot.exists()) {
        setBaseUserData(snapshot.data() as UserData);
      } else {
        setBaseUserData(null);
      }
    });

    return unsubscribe;
  }, [user]);

  // ---------------------------
  // WALLETS
  // ---------------------------
  useEffect(() => {
    if (!user) return;

    const refWallets = collection(db, 'users', user.uid, 'wallets');

    const unsubscribe = onSnapshot(
      refWallets,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const next: WalletDoc[] = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            leagueId: String(data.leagueId),
            seasonId: Number(data.seasonId),
            type: data.type,
            available: Number(data.available ?? 0),
            purchased: Number(data.purchased ?? 0),
            gifted: Number(data.gifted ?? 0),
            earned: Number(data.earned ?? 0),
            claimed: Array.isArray(data.claimed) ? data.claimed : [],
          };
        });

        setWallets(next);
        setLoading(false);
      },
      () => {
        setWallets([]);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [user]);

  // ---------------------------
  // MERGE USER + WALLETS
  // ---------------------------
  const userData = useMemo<UserData | null>(() => {
    if (!baseUserData) return null;

    const merged: UserData = {
      ...baseUserData,
      leagues: {
        ...(baseUserData.leagues ?? {}),
      },
    };

    for (const wallet of wallets) {
      const leagueId = wallet.leagueId;
      const seasonId = String(wallet.seasonId);
      const mode = wallet.type;

      if (!merged.leagues) merged.leagues = {};

      if (!merged.leagues[leagueId]) {
        merged.leagues[leagueId] = {
          seasons: {},
        };
      }

      if (!merged.leagues[leagueId].seasons[seasonId]) {
        merged.leagues[leagueId].seasons[seasonId] = {
          main: {
            cardUrl: null,
            tokens: {
              selected: [],
              totalSelected: 0,
            },
            stats: {
              purchased: 0,
              gifted: 0,
              referral: 0,
              carryOver: 0,
              totalAvailable: 0,
            },
          },
          arena: {
            tokens: {
              selected: [],
              totalSelected: 0,
            },
            stats: {
              earnedFromPurchase: 0,
              earnedFromReferral: 0,
              carryOver: 0,
              totalAvailable: 0,
            },
          },
        };
      }

      const season = merged.leagues[leagueId].seasons[seasonId];

      if (mode === 'main') {
        const existingTokens = season.main.tokens ?? {
          selected: [],
          totalSelected: 0,
        };

        const stats: UserSeasonStats = {
          purchased: wallet.purchased,
          gifted: wallet.gifted,
          referral: wallet.earned,
          carryOver: 0,
          totalAvailable: wallet.available,
        };

        season.main = {
          ...(season.main ?? {}),
          cardUrl: season.main?.cardUrl ?? null,
          tokens: existingTokens,
          stats,
        };
      }

      if (mode === 'arena') {
        const existingTokens = season.arena.tokens ?? {
          selected: [],
          totalSelected: 0,
        };

        const stats: UserArenaStats = {
          earnedFromPurchase: wallet.earned,
          earnedFromReferral: 0,
          carryOver: 0,
          totalAvailable: wallet.available,
        };

        season.arena = {
          ...(season.arena ?? {}),
          tokens: existingTokens,
          stats,
        };
      }
    }

    return merged;
  }, [baseUserData, wallets]);

  // ---------------------------
  // UPDATE SIMPLE
  // ---------------------------
  const updateUserField = useCallback(
    async <K extends keyof UserData>(key: K, value: UserData[K]) => {
      if (!user) return;

      const refUser = doc(db, 'users', user.uid);

      await setDoc(refUser, { [key]: value }, { merge: true });
    },
    [user],
  );

  // ---------------------------
  // UPDATE NESTED
  // ---------------------------
  const updateNestedField = useCallback(
    async (path: string, value: any) => {
      if (!user) return;

      const refUser = doc(db, 'users', user.uid);

      await setDoc(refUser, { [path]: value }, { merge: true });
    },
    [user],
  );

  // ---------------------------
  // TOKEN SELECTION
  // ---------------------------
  const addTokenSelection = useCallback(
    async ({
      leagueId,
      seasonId,
      mode,
      tokenId,
    }: {
      leagueId: string;
      seasonId: string;
      mode: 'main' | 'arena';
      tokenId: number;
    }) => {
      if (!user) return;

      // const refUser = doc(db, 'users', user.uid);

      const current =
        userData?.leagues?.[leagueId]?.seasons?.[seasonId]?.[mode];

      const selected = current?.tokens?.selected ?? [];

      if (selected.includes(tokenId)) return;

      // const updatedSelected = [...selected, tokenId];

      // const path = `leagues.${leagueId}.seasons.${seasonId}.${mode}.tokens`;

      // await setDoc(
      //   refUser,
      //   {
      //     [path]: {
      //       selected: updatedSelected,
      //       totalSelected: updatedSelected.length,
      //     },
      //   },
      //   { merge: true },
      // );
    },
    [user, userData],
  );

  // ---------------------------
  // LEGACY METHODS (kept)
  // ---------------------------
  const registerPurchase = useCallback(async () => {}, []);
  const registerReferralReward = useCallback(async () => {}, []);

  // ---------------------------
  // MAIN STATS
  // ---------------------------
  const getMainStats = useCallback(
    ({ leagueId, seasonId }: { leagueId: string; seasonId: string }) => {
      const stats =
        userData?.leagues?.[leagueId]?.seasons?.[seasonId]?.main?.stats;

      const totalEarned = stats?.totalAvailable ?? 0;

      const claimed =
        userData?.leagues?.[leagueId]?.seasons?.[seasonId]?.main?.tokens
          ?.totalSelected ?? 0;

      return {
        totalEarned,
        claimed,
        available: Math.max(0, totalEarned - claimed),
      };
    },
    [userData],
  );

  // ---------------------------
  // ARENA STATS
  // ---------------------------
  const getArenaStats = useCallback(
    ({ leagueId, seasonId }: { leagueId: string; seasonId: string }) => {
      const stats =
        userData?.leagues?.[leagueId]?.seasons?.[seasonId]?.arena?.stats;

      const totalEarned = stats?.totalAvailable ?? 0;

      const claimed =
        userData?.leagues?.[leagueId]?.seasons?.[seasonId]?.arena?.tokens
          ?.totalSelected ?? 0;

      return {
        totalEarned,
        claimed,
        available: Math.max(0, totalEarned - claimed),
      };
    },
    [userData],
  );

  return {
    userData,
    loading,
    updateUserField,
    updateNestedField,
    addTokenSelection,
    registerPurchase,
    registerReferralReward,
    getMainStats,
    getArenaStats,
  };
};
